/*
  Developers: Sakshi Bhise
  File: AppProvider.tsx
  Date: 06/18/2025
  Project Name: AskHR
  Team: DSET

  Purpose:
  Implements global state management for the AskHR frontend using React Context and useReducer.
  Provides a centralized state store for chat, user data, frontend settings, health checks, and languages.
  Enables all child components to access and update the state via context for consistency and scalability.
*/

//---------------- Imports -------------------//
import React, { createContext, useReducer, ReactNode, useEffect, useContext } from 'react';
import { appStateReducer } from './AppReducer';
import {
  Conversation,
  ChatHistoryLoadingState,
  SQLDBHealth,
  historyList,
  historyEnsure,
  SQLDBStatus,
  frontendSettings,
  FrontendSettings,
  Feedback,
  updateLastLogin,
  getUserDetails,
  getLanguages,
  getUserInfo,
  User,
} from '../api';
import { AuthContext } from './AuthContext';

// Define the structure of the application's state  
export interface AppState {
  isChatHistoryOpen: boolean; // Whether the chat history panel is open  
  chatHistoryLoadingState: ChatHistoryLoadingState; // Loading state of chat history  
  isSqlDBAvailable: SQLDBHealth; // SqlDB availability status  
  chatHistory: Conversation[] | null; // All chat history  
  filteredChatHistory: Conversation[] | null; // Filtered chat history for search/filter  
  currentChat: Conversation | null; // Currently selected chat  
  frontendSettings: FrontendSettings | null; // Current frontend settings  
  feedbackState: { [answerId: string]: Feedback.Neutral | Feedback.Positive | Feedback.Negative }; // Feedback state by answer
  selectedUseCase: string | null; // Current use case
  selectedCard: number | 0; // Current selected card in UI
  impUserCountry: string | null;// impersonating users country
  userLanguage: string | "en";// user's language
  userGId: string | null;// user's global id 
  userEmployeeGroup: string | null;// user employee group
  userPermission: any | null;// user's permission group
  userPreferredLanguage: string | null;// user's preferred language
  isImpersonating: boolean;// support mode status
  ImpersonatedUser: string | null;// support user
  loggedInUser: string | null;// logged in user,
  loggedInUserName: User | null // User's First Name and last Name
  availableLanguages: any[] | null; //available languages
  termsAcceptedAt: Date | null; // whether the user has accepted the terms or not.
  loggedInUserCountry: string | null; // loggedin user country
  isSupportUser: boolean; // whether user is a support user (skips Interact API, uses DB permissions)

}

// Define types of actions for reducer  
export type Action =
  | { type: 'TOGGLE_CHAT_HISTORY' }
  | { type: 'SET_SQLDB_STATUS'; payload: SQLDBHealth }
  | { type: 'UPDATE_CHAT_HISTORY_LOADING_STATE'; payload: ChatHistoryLoadingState }
  | { type: 'UPDATE_CURRENT_CHAT'; payload: Conversation | null }
  | { type: 'UPDATE_FILTERED_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'UPDATE_CHAT_HISTORY'; payload: Conversation }
  | { type: 'UPDATE_CHAT_TITLE'; payload: Conversation }
  | { type: 'DELETE_CHAT_ENTRY'; payload: string }
  | { type: 'DELETE_CHAT_HISTORY' }
  | { type: 'DELETE_CURRENT_CHAT_MESSAGES'; payload: string }
  | { type: 'FETCH_CHAT_HISTORY'; payload: Conversation[] | null }
  | { type: 'FETCH_FRONTEND_SETTINGS'; payload: FrontendSettings | null }
  | { type: 'SET_FEEDBACK_STATE'; payload: { answerId: string; feedback: Feedback.Positive | Feedback.Negative | Feedback.Neutral } }
  | { type: 'GET_FEEDBACK_STATE'; payload: string }
  | { type: 'SET_SELECTED_USE_CASE'; payload: string | null }
  | { type: 'ENABLE_CHAT_HISTORY' }
  | { type: 'DISABLE_CHAT_HISTORY' }
  | { type: 'APPEND_CHAT_HISTORY'; payload: Conversation[] }
  | { type: 'SET_SELECTED_CARD'; payload: number | 0 }
  | { type: 'IMP_USER_COUNTRY'; payload: string | null }
  | { type: 'USER_LANGUAGE'; payload: string | "en" }
  | { type: 'USER_GID'; payload: string | null }
  | { type: 'USER_EMPLOYEE_GROUP'; payload: string | null }
  | { type: 'USER_PERMISSION'; payload: any | null }
  | { type: 'IS_SUPPORT_USER'; payload: boolean }
  | { type: 'IS_IMPERSONATING'; payload: boolean | false }
  | { type: 'USER_PREFERRED_LANGUAGE'; payload: string | null }
  | { type: 'IMPERSONATED_USER'; payload: string | null }
  | { type: 'LOGGED_IN_USER'; payload: string | null }
  | { type: 'LOGGED_IN_USER_NAME'; payload: User | null }
  | { type: 'TERMS_ACCEPTED_AT'; payload: Date | null }
  | { type: 'SET_AVAILABLE_LANGUAGES'; payload: any[] | null }
  | { type: 'SET_LOGGED_IN_USER_COUNTRY'; payload: string | null };


// Define the initial state for the app  
const initialState: AppState = {
  isChatHistoryOpen: false,
  chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
  chatHistory: null,
  filteredChatHistory: null,
  currentChat: null,
  isSqlDBAvailable: {
    sqlDB: false,
    status: SQLDBStatus.NotConfigured,
  },
  frontendSettings: null,
  feedbackState: {},
  selectedUseCase: null,
  selectedCard: 0,
  impUserCountry: null,
  userLanguage: "en",
  userGId: null,
  userEmployeeGroup: null,
  userPermission: null,
  userPreferredLanguage: null,
  isImpersonating: false,
  ImpersonatedUser: null,
  loggedInUser: null,
  termsAcceptedAt: null,
  availableLanguages: [],
  loggedInUserCountry: null,
  loggedInUserName: null,
  isSupportUser: false
};

// Create a React Context for App State and Dispatcher  
export const AppStateContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<Action>;
} | undefined>(undefined);

// Define Props for the Provider  
type AppStateProviderProps = {
  children: ReactNode; // Wrapped children components  
};

// AppStateProvider supplies global state and dispatcher to all nested components  
export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
  const backend_url = import.meta.env.VITE_BACKEND_URL || ""
  const [state, dispatch] = useReducer(appStateReducer, initialState);
  const { token } = useContext(AuthContext);

  // On mount: fetch chat history and SqlDB health status
  useEffect(() => {
    if (state.userEmployeeGroup === null) return;

    if (state.userEmployeeGroup === 'user_not_found') {
      dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
      return;
    }

    const allowed = state.userEmployeeGroup?.toLowerCase() !== "contingent worker";

    if (!allowed) {
      // Optionally clear history, set loading to idle/failed if needed
      dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
      return;
    }
    const fetchChatHistory = async (offset = 0, count = 10): Promise<Conversation[] | null> => {
      try {
        const result = await historyList(offset, state.userGId, token, count);
        if (result) {
          dispatch({ type: 'FETCH_CHAT_HISTORY', payload: result });
        } else {
          dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null });
        }
        return result;
      } catch (err) {
        dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
        dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null });
        //console.error('Error fetching chat history:', err);  
        return null;
      }
    };

    const getHistoryEnsure = async () => {
      dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Loading });
      try {
        const response = await historyEnsure(token);
        if (response) {
          const res = await fetchChatHistory();
          if (res) {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Success });
            dispatch({ type: 'SET_SQLDB_STATUS', payload: response });
          } else {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
            dispatch({
              type: 'SET_SQLDB_STATUS',
              payload: { sqlDB: false, status: SQLDBStatus.NotWorking },
            });
          }
        } else {
          await fetchChatHistory();
          dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
          dispatch({ type: 'SET_SQLDB_STATUS', payload: response });
        }
      } catch (err) {
        dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
        dispatch({
          type: 'SET_SQLDB_STATUS',
          payload: { sqlDB: false, status: SQLDBStatus.NotConfigured },
        });
        //console.error('Error ensuring chat history:', err);  
      }
    };

    if (!state.isImpersonating) {
      getHistoryEnsure();
    }
  }, [token, state.userEmployeeGroup]);

  // On mount: fetch frontend settings (UI config)  
  useEffect(() => {
    const getFrontendSettings = async () => {
      try {
        const response = await frontendSettings(token);
        dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: response as FrontendSettings });
      } catch (err) {
        //console.error('Error fetching frontend settings:', err);  
      }
    };
    getFrontendSettings();
  }, [token]);

  // On mount: fetch user details (country, language, ID, permissions, etc)
  useEffect(() => {

    const fetchUserDetails = async () => {
      try {
        const response = await getUserDetails(token)
        const data = await response.json();
        if (response.status === 404) {
          dispatch({ type: 'USER_EMPLOYEE_GROUP', payload: 'user_not_found' });
          dispatch({ type: 'SET_LOGGED_IN_USER_COUNTRY', payload: 'user_not_found' });
          return;
        }
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        // dispatch({ type: 'USER_COUNTRY', payload: data.Country });
        dispatch({ type: 'SET_LOGGED_IN_USER_COUNTRY', payload: data.Country })
        dispatch({ type: 'USER_GID', payload: data.GlobalID });
        dispatch({ type: 'USER_EMPLOYEE_GROUP', payload: data.EmployeeGroup });
        dispatch({ type: 'USER_PERMISSION', payload: data.AllGroupIDs });
        dispatch({ type: 'TERMS_ACCEPTED_AT', payload: data.TermsAcceptedAt });
        dispatch({ type: 'IS_SUPPORT_USER', payload: !!data.IsSupportUser });

        if (!state.loggedInUser) {
          updateLastLogin(token).catch(err => {
            console.error("Failed to update last login:", err);
          });
        }
      } catch (err) {
        dispatch({ type: 'IMP_USER_COUNTRY', payload: 'notfound' });
        dispatch({ type: 'SET_LOGGED_IN_USER_COUNTRY', payload: 'notfound' })
        dispatch({ type: 'USER_EMPLOYEE_GROUP', payload: 'user_not_found' });
        console.error(err);
      }
    };

    if (!state.isImpersonating) {
      fetchUserDetails();
    }
  }, [token, state.isImpersonating]);

  useEffect(() => {
    if (state.userEmployeeGroup === null || state.userEmployeeGroup === 'user_not_found') return;

    const allowed = state.userEmployeeGroup?.toLowerCase() !== "contingent worker";
    const fetchLanguages = async () => {
      try {
        const response = await getLanguages(token);
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();

        dispatch({ type: "USER_LANGUAGE", payload: data.language });
        dispatch({ type: "USER_PREFERRED_LANGUAGE", payload: data.preferred_language });

        if (data.languages) {
          dispatch({ type: "SET_AVAILABLE_LANGUAGES", payload: data.languages });
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (allowed && !state.isImpersonating && state.userEmployeeGroup && state.userEmployeeGroup !== 'user_not_found') {
      fetchLanguages();
    }
  }, [token, state.isImpersonating, state.userEmployeeGroup]);

  /**
    * Effect: Fetches and parses the current user's identity on component mount.
    * Falls back to a local dev placeholder when running on localhost.
    */
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const userInfoList = await getUserInfo(token);
        dispatch({ type: 'LOGGED_IN_USER', payload: userInfoList?.upn });
        dispatch({ type: 'LOGGED_IN_USER_NAME', payload: { firstName: userInfoList?.given_name ?? "", lastName: userInfoList?.family_name } })

      } catch (err) {
        console.error("Error loading user info:", err);
      }
    };

    loadUserInfo();
  }, [token]);


  // Provide the state and dispatcher to children via Context  
  return (
    <AppStateContext.Provider value={{ state, dispatch }}>
      {children}
    </AppStateContext.Provider>
  );
};  